import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '@/components/ui/select';
import { fetchSpecies } from '@/services/lookups';

import './SpeciesDropdown.css';

// Bovine=100000000, Caprine=100000002, Ovine=100000004
const BOVINE_SMALL_RUMINANT_VALUES = [100000000, 100000002, 100000004];
const BOVINE_SMALL_RUMINANT_KEY = 'bovine_small_ruminant';

type SpeciesOption = {
  label: string;
  key: string;
  subFamilies: number[];
};

type SpeciesDropdownProps = {
  className?: string;
  label?: string;
  onChange: (subFamilies: number[]) => void;
  onUserChange?: (subFamilies: number[]) => void;
  disabled?: boolean;
  initialSubFamily?: number | null;
  tabIndex?: number;
};

function getKeyForSubFamily(subFamily: number | null | undefined): string {
  if (subFamily == null) return BOVINE_SMALL_RUMINANT_KEY;
  if (BOVINE_SMALL_RUMINANT_VALUES.includes(subFamily))
    return BOVINE_SMALL_RUMINANT_KEY;
  return String(subFamily);
}

function getSubFamiliesForKey(key: string): number[] {
  if (key === BOVINE_SMALL_RUMINANT_KEY) return BOVINE_SMALL_RUMINANT_VALUES;
  return [Number(key)];
}

export const SpeciesDropdown = ({
  className,
  label,
  onChange,
  onUserChange,
  disabled,
  initialSubFamily,
  tabIndex,
}: SpeciesDropdownProps) => {
  const { t } = useTranslation();

  const defaultLabel = `${t('species.bovine', 'Bovine')} / ${t('species.smallRuminant', 'Small Ruminant')}`;
  const defaultOption: SpeciesOption = {
    label: defaultLabel,
    key: BOVINE_SMALL_RUMINANT_KEY,
    subFamilies: BOVINE_SMALL_RUMINANT_VALUES,
  };

  const initialKey = getKeyForSubFamily(initialSubFamily);
  const [options, setOptions] = useState<SpeciesOption[]>([defaultOption]);
  const [selectedKey, setSelectedKey] = useState<string>(initialKey);
  const hasUserChangedRef = useRef(false);
  const lastInitialKeyRef = useRef<string>(initialKey);

  // Sync selectedKey when initialSubFamily changes (e.g., async fetch resolves)
  // BUT ignore once the user has actively chosen a species — late prop updates
  // must not clobber the user's selection.
  useEffect(() => {
    if (hasUserChangedRef.current) return;
    if (lastInitialKeyRef.current === initialKey) return;
    lastInitialKeyRef.current = initialKey;
    setSelectedKey(initialKey);
  }, [initialKey]);

  const selectedLabel = useMemo(() => {
    const option = options.find((o) => o.key === selectedKey);
    return option?.label ?? '';
  }, [options, selectedKey]);

  const loadSpecies = useCallback(async () => {
    try {
      const species = await fetchSpecies();

      const otherOptions: SpeciesOption[] = species
        .filter((s) => !BOVINE_SMALL_RUMINANT_VALUES.includes(s.value))
        .map((s) => ({
          label: t(`species.${s.name.toLowerCase()}`, s.name),
          key: String(s.value),
          subFamilies: [s.value],
        }));

      setOptions([defaultOption, ...otherOptions]);
    } catch {
      // Keep default option even on error
    }
  }, [t]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadSpecies();
  }, [loadSpecies]);

  useEffect(() => {
    const subFamilies = getSubFamiliesForKey(initialKey);
    onChange(subFamilies);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (value: string | null) => {
    // Base UI Select can call onValueChange(null) when the controlled value
    // becomes unregistered (e.g., during item registry changes). Ignore null
    // to prevent clobbering the user's selection.
    if (value == null) return;

    hasUserChangedRef.current = true;
    setSelectedKey(value);
    const option = options.find((o) => o.key === value);
    const subFamilies = option?.subFamilies ?? getSubFamiliesForKey(value);
    onChange(subFamilies);
    onUserChange?.(subFamilies);
  };

  return (
    <div
      className={`label-input-group species-dropdown${className ? ` ${className}` : ''}`}
    >
      {label && <label>{label}</label>}
      <Select value={selectedKey} onChange={handleChange} disabled={disabled}>
        <SelectTrigger className="species-select-trigger" tabIndex={tabIndex}>
          <span className="species-select-value">{selectedLabel}</span>
        </SelectTrigger>
        <SelectContent className="species-select-content">
          {options.map((option) => (
            <SelectItem key={option.key} value={option.key}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};
